import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Lock as LockIcon,
  Settings as SettingsIcon,
  Web as WebIcon,
  DesktopWindowsOutlined as DesktopIcon,
  Person as PersonIcon,
  Lightbulb as LightbulbIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
  ScreenRotation as ScreenRotationIcon,
  Computer as ComputerIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Chat as ChatIcon,
  VolumeOff as VolumeOffIcon,
  VolumeUp as VolumeUpIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../theme/CustomThemeProvider';
import { createApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/authUtils';
import tokenService from '../utils/tokenService';
import { useSnackbar } from './CentralSnackbar';
import { useWebSocket } from '../contexts/WebSocketContext';

// Hilfsfunktion für MTR Popup-Optionen basierend auf Orientierung
const getMTRPopupOptions = (orientation) => {
  console.log('🔧 MTRDashboard getMTRPopupOptions aufgerufen mit orientation:', orientation);
  
  const screenWidth = window.screen.availWidth;
  const screenHeight = window.screen.availHeight;
  const screenLeft = window.screen.availLeft || 0;
  const screenTop = window.screen.availTop || 0;
  
  console.log('📺 MTRDashboard Screen Info:', {
    width: screenWidth,
    height: screenHeight,
    left: screenLeft,
    top: screenTop
  });
  
  let width, height, left, top, popupOptions;
  
  if (orientation === 'landscape') {
    // Landscape: Am unteren Bildschirmrand, so breit wie der Bildschirm
    width = screenWidth;
    height = 300;
    left = screenLeft;
    top = screenTop + screenHeight - height;
    
    console.log('🏞️ MTRDashboard LANDSCAPE Berechnung:', {
      width, height, left, top,
      calculation: `screenTop(${screenTop}) + screenHeight(${screenHeight}) - height(${height}) = ${top}`
    });
  } else {
    // Portrait: Am linken Bildschirmrand, so hoch wie der Bildschirm  
    width = 300;
    height = screenHeight;
    left = screenLeft;
    top = screenTop;
    
    console.log('📱 MTRDashboard PORTRAIT Berechnung:', {
      width, height, left, top
    });
  }
  
  popupOptions = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,menubar=no,toolbar=no,location=no`;
  console.log('✅ MTRDashboard Finale Popup-Optionen:', popupOptions);
  
  return popupOptions;
};

const MTRDashboard = ({ onLogout }) => {
  const { t } = useTranslation();
  const { mode, toggleTheme } = useThemeContext();
  const { showSuccess, showError } = useSnackbar();
  const { newMessage, clearNewMessage } = useWebSocket();
  
  // User data state
  const [currentUser, setCurrentUser] = useState(null);
  
  // Sound notification ref
  const audioRef = useRef(null);
  const lastNotificationTime = useRef(0);
  const cockpitOpenedRef = useRef(false);
  
  // Username change state
  const [usernameData, setUsernameData] = useState({
    new_username: ''
  });
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [usernameSuccess, setUsernameSuccess] = useState('');
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Viewer preference state
  const [viewerPreference, setViewerPreference] = useState('web');
  const [viewerConfig, setViewerConfig] = useState({
    web: { base_url: '', test_study_uid: '' },
    client: { base_url: '', test_study_uid: '' }
  });
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState('');
  const [viewerSuccess, setViewerSuccess] = useState('');

  // Cockpit Orientation state
  const [orientation, setOrientation] = useState('portrait'); // Default Portrait

  // Workstation state
  const [workstations, setWorkstations] = useState([]);
  const [selectedWorkstation, setSelectedWorkstation] = useState('');
  const [currentWorkstation, setCurrentWorkstation] = useState(null);
  const [workstationLoading, setWorkstationLoading] = useState(false);

  // Chat Settings State
  const [chatSettings, setChatSettings] = useState({
    muted: false,
    notificationSound: 'Bright'
  });
  const [chatLoading, setChatLoading] = useState(false);

  // Load user data and viewer config on component mount
  useEffect(() => {
    const init = async () => {
      let user = null;
      try {
        const meResp = await fetch(createApiUrl('auth/me'), { headers: getAuthHeaders() });
        if (meResp.ok) {
          user = await meResp.json();
        }
      } catch (e) {
        console.warn('MTRDashboard: Konnte User nicht laden', e);
      }
      setCurrentUser(user);
      loadViewerData();
      loadOrientation(user);
      loadWorkstations(user);
      loadChatSettings();
    };
    init();
  }, []);

  // Auto-open MTR Cockpit popup on mount (previously done in Login.js)
  useEffect(() => {
    if (cockpitOpenedRef.current) return;
    cockpitOpenedRef.current = true;

    const timer = setTimeout(() => {
      const savedOrientation = localStorage.getItem('mtr-orientation') || orientation;
      const popupOptions = getMTRPopupOptions(savedOrientation);
      const popupWindow = window.open('/radiographer-cockpit', 'mtr-dashboard', popupOptions);
      if (popupWindow) popupWindow.focus();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Sound notification handler - nur wenn Cockpit geschlossen ist
  useEffect(() => {
    if (!newMessage || chatSettings.muted) return;

    // Prüfe ob das MTR-Cockpit-Popup offen ist
    const isCockpitOpen = () => {
      try {
        // Versuche auf das Popup-Fenster zuzugreifen (OHNE es zu schließen!)
        const popupWindows = window.open('', 'mtr-dashboard');
        // Wenn es ein gültiges Fenster ist und nicht geschlossen, ist es offen
        const isOpen = popupWindows && !popupWindows.closed;
        // WICHTIG: Nicht close() aufrufen, das würde das Popup schließen!
        return isOpen;
      } catch (e) {
        return false;
      }
    };

    // Spiele Sound nur ab, wenn Cockpit NICHT offen ist
    if (!isCockpitOpen()) {
      // Debounce: Nur alle 2 Sekunden eine Benachrichtigung
      const now = Date.now();
      if (now - lastNotificationTime.current > 2000) {
        lastNotificationTime.current = now;
        
        const soundFile = `/sounds/${chatSettings.notificationSound}.mp3`;
        
        // Erstelle oder verwende vorhandenes Audio-Element
        if (!audioRef.current) {
          audioRef.current = new Audio(soundFile);
        } else {
          audioRef.current.src = soundFile;
        }
        
        audioRef.current.play().catch(err => {
          console.warn('Konnte Benachrichtigungssound nicht abspielen:', err);
        });
      }
    }

    clearNewMessage();
  }, [newMessage, chatSettings.muted, chatSettings.notificationSound, clearNewMessage]);

  const loadOrientation = async (user) => {
    // Versuche zuerst frische task_settings aus der API zu laden
    try {
      console.log('Dashboard: Lade Orientierung aus frischen task_settings...');
      const settingsResponse = await fetch(createApiUrl('profile/task-settings'), {
        headers: getAuthHeaders()
      });
      
      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        console.log('Dashboard: Frische task_settings für Orientierung erhalten:', settingsData);
        
        if (settingsData.task_settings) {
          const savedSettings = typeof settingsData.task_settings === 'string' 
            ? JSON.parse(settingsData.task_settings) 
            : settingsData.task_settings;
          
          if (savedSettings.mtr_orientation) {
            console.log('Dashboard: Setze Orientierung auf:', savedSettings.mtr_orientation);
            setOrientation(savedSettings.mtr_orientation);
            return;
          }
        }
      } else {
        console.warn('Dashboard: Konnte task_settings nicht laden für Orientierung:', settingsResponse.status);
      }
    } catch (error) {
      console.error('Dashboard: Fehler beim Laden der task_settings für Orientierung:', error);
    }

    // Fallback: Versuche aus cached User-Daten zu laden
    if (user?.task_settings) {
      try {
        const savedSettings = JSON.parse(user.task_settings);
        if (savedSettings.mtr_orientation) {
          console.log('Dashboard: Verwende cached Orientierung:', savedSettings.mtr_orientation);
          setOrientation(savedSettings.mtr_orientation);
        }
      } catch (e) {
        console.warn('Fehler beim Laden der Orientierung aus cached Daten:', e);
      }
    }
  };

  const loadViewerData = async () => {
    try {
      // Load viewer configuration
      const configResponse = await fetch(createApiUrl('profile/viewer-config'), {
        headers: { ...getAuthHeaders() }
      });
      const configData = await configResponse.json();
      
      if (configData.success) {
        setViewerConfig(configData.viewer_config);
      }
      
      // Load user's viewer preference
      const prefResponse = await fetch(createApiUrl('profile/viewer-preference'), {
        headers: { ...getAuthHeaders() }
      });
      const prefData = await prefResponse.json();
      
      if (prefData.viewer_preference) {
        setViewerPreference(prefData.viewer_preference);
      }
    } catch (error) {
      console.error('Error loading viewer data:', error);
    }
  };

  const handleUsernameChange = async () => {
    if (!usernameData.new_username) {
      setUsernameError(t('profile.usernameChange.newUsernameRequired'));
      return;
    }

    if (usernameData.new_username === currentUser?.username) {
      setUsernameError(t('profile.usernameChange.sameUsername'));
      return;
    }

    setUsernameLoading(true);
    setUsernameError('');
    setUsernameSuccess('');

    try {
      const response = await fetch(createApiUrl('profile/change-username'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ 
          newUsername: usernameData.new_username
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setUsernameSuccess(data.message || t('profile.usernameChange.changeSuccess'));
        setUsernameData({ new_username: '' });
        // Update current user with new username
        const updatedUser = { ...currentUser, username: data.new_username };
        setCurrentUser(updatedUser);
        // Update session storage
        const sessionData = JSON.parse(sessionStorage.getItem('reportingSession') || '{}');
        if (sessionData.user) {
          sessionData.user.username = data.new_username;
          sessionStorage.setItem('reportingSession', JSON.stringify(sessionData));
        }
      } else {
        setUsernameError(data.message || t('profile.usernameChange.changeError'));
      }
    } catch (error) {
      setUsernameError(t('profile.usernameChange.changeError'));
    } finally {
      setUsernameLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError(t('login.forcePasswordChangeDialog.errorMismatch'));
      return;
    }

    if (!passwordData.new_password) {
      setPasswordError(t('profile.password.newPasswordRequired'));
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      const response = await fetch(createApiUrl('profile/change-password'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ 
          currentPassword: passwordData.current_password,
          newPassword: passwordData.new_password 
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setPasswordSuccess(data.message || t('profile.password.changeSuccess'));
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        setPasswordError(data.message || t('profile.password.changeError'));
      }
    } catch (error) {
      setPasswordError(t('profile.password.changeError'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleViewerPreferenceChange = async (newPreference) => {
    if (newPreference === viewerPreference) return;

    setViewerLoading(true);
    setViewerError('');
    setViewerSuccess('');

    try {
      const response = await fetch(createApiUrl('profile/viewer-preference'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ viewer_preference: newPreference })
      });

      const data = await response.json();
      if (data.success) {
        setViewerPreference(newPreference);
        setViewerSuccess(data.message || t('profile.viewer.updateSuccess'));
        setTimeout(() => setViewerSuccess(''), 5000);
      } else {
        setViewerError(data.message || t('profile.viewer.updateError'));
      }
    } catch (error) {
      setViewerError(t('profile.viewer.updateError'));
    } finally {
      setViewerLoading(false);
    }
  };

  // Orientierung wechseln und Popup neu öffnen
  const handleOrientationToggle = async () => {
    const newOrientation = orientation === 'landscape' ? 'portrait' : 'landscape';
    
    try {
      // 1. WICHTIG: Aktuelle task_settings aus der Datenbank laden
      console.log('Dashboard: Lade aktuelle task_settings vor Orientierungsänderung...');
      const settingsResponse = await fetch(createApiUrl('profile/task-settings'), {
        headers: getAuthHeaders()
      });
      
      let currentSettings = {};
      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        console.log('Dashboard: Frische task_settings erhalten:', settingsData);
        try {
          currentSettings = settingsData.task_settings 
            ? (typeof settingsData.task_settings === 'string' 
                ? JSON.parse(settingsData.task_settings) 
                : settingsData.task_settings)
            : {};
        } catch (e) {
          console.warn('Dashboard: Fehler beim Parsen der task_settings, verwende leere Einstellungen:', e);
          currentSettings = {};
        }
      } else {
        console.warn('Dashboard: Konnte task_settings nicht laden, verwende cached Version');
        try {
          currentSettings = currentUser?.task_settings ? JSON.parse(currentUser.task_settings) : {};
        } catch (e) {
          console.warn('Dashboard: Fehler beim Parsen der cached task_settings:', e);
          currentSettings = {};
        }
      }
      
      const updatedSettings = {
        ...currentSettings,
        mtr_orientation: newOrientation
      };
      
      console.log('Vor Orientierungsänderung - currentSettings:', currentSettings);
      console.log('Nach Orientierungsänderung - updatedSettings:', updatedSettings);
      
      const response = await fetch(createApiUrl('profile/task-settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ task_settings: JSON.stringify(updatedSettings) })
      });
      
      if (response.ok) {
        console.log('Orientierung gespeichert:', updatedSettings);
        
        // 2. State aktualisieren
        setOrientation(newOrientation);
        
        // 3. Über localStorage dem Popup die Orientierungsänderung mitteilen
        const orientationChangeEvent = {
          orientation: newOrientation,
          timestamp: Date.now()
        };
        localStorage.setItem('mtr-orientation-change', JSON.stringify(orientationChangeEvent));
        console.log('Orientierungsänderung über localStorage gesendet:', orientationChangeEvent);
        
        // 3.1 Trigger storage event manually (für Fallback)
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'mtr-orientation-change',
          newValue: JSON.stringify(orientationChangeEvent),
          url: window.location.href
        }));
        
        // 4. Versuche alle offenen MTR Popups zu schließen
        // (Das funktioniert nur begrenzt wegen Browser-Sicherheit)
        try {
          // Alle offenen Fenster mit dem Namen 'mtr-dashboard' schließen
          const existingWindow = window.open('', 'mtr-dashboard');
          if (existingWindow) {
            existingWindow.close();
          }
        } catch (e) {
          console.log('Konnte bestehendes Popup nicht automatisch schließen:', e);
        }
        
        // 5. Neues Popup mit neuer Orientierung öffnen
        setTimeout(() => {
          console.log('🔄 MTR DASHBOARD: Orientierung wechseln - Öffne neues Popup mit:', newOrientation);
          
          const popupOptions = getMTRPopupOptions(newOrientation);
          console.log('🔄 MTR DASHBOARD: window.open wird aufgerufen mit:', {
            url: '/radiographer-cockpit',
            windowName: 'mtr-dashboard', 
            options: popupOptions,
            newOrientation: newOrientation
          });
            
          const popupWindow = window.open(
            '/radiographer-cockpit', 
            'mtr-dashboard',
            popupOptions
          );
          
          if (popupWindow) {
            popupWindow.focus();
          }
        }, 100);
      } else {
        console.error('Fehler beim Speichern der Orientierung:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Fehler beim Wechseln der Orientierung:', error);
    }
  };

  // Workstation functions
  const loadWorkstations = async (user = null) => {
    try {
      const response = await fetch(createApiUrl('admin/workstations'), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setWorkstations(data);
        
        // Check if current user is logged into any workstation
        // Use passed user or currentUser from state
        const userToCheck = user || currentUser;
        if (userToCheck) {
          const loggedIn = data.find(ws => 
            ws.users_logged_in && ws.users_logged_in.includes(userToCheck.id)
          );
          setCurrentWorkstation(loggedIn || null);
        }
      }
    } catch (error) {
      console.error('Error loading workstations:', error);
    }
  };

  const handleWorkstationLogin = async () => {
    if (!selectedWorkstation) return;
    
    setWorkstationLoading(true);
    
    try {
      const response = await fetch(createApiUrl(`admin/workstations/${selectedWorkstation}/login`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      if (data.success) {
        showSuccess(t('mtr.workstation.loginSuccess', 'Erfolgreich am Arbeitsplatz eingeloggt'));
        await loadWorkstations(); // Reload to update current workstation
        setSelectedWorkstation('');
      } else {
        showError(data.message || t('mtr.workstation.loginError', 'Fehler beim Einloggen'), 'ERR-WS-LOGIN');
      }
    } catch (error) {
      showError(t('mtr.workstation.loginError', 'Fehler beim Einloggen'), 'ERR-WS-LOGIN-EX');
    } finally {
      setWorkstationLoading(false);
    }
  };

  const handleWorkstationLogout = async () => {
    if (!currentWorkstation) return;
    
    setWorkstationLoading(true);
    
    try {
      const response = await fetch(createApiUrl(`admin/workstations/${currentWorkstation.id}/logout`), {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      if (data.success) {
        showSuccess(t('mtr.workstation.logoutSuccess', 'Erfolgreich vom Arbeitsplatz ausgeloggt'));
        await loadWorkstations(); // Reload to update current workstation
      } else {
        showError(data.message || t('mtr.workstation.logoutError', 'Fehler beim Ausloggen'), 'ERR-WS-LOGOUT');
      }
    } catch (error) {
      showError(t('mtr.workstation.logoutError', 'Fehler beim Ausloggen'), 'ERR-WS-LOGOUT-EX');
    } finally {
      setWorkstationLoading(false);
    }
  };

  // Load Chat Settings
  const loadChatSettings = async () => {
    try {
      const response = await fetch(createApiUrl('profile/chat-settings'), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.chat_settings) {
          setChatSettings(data.chat_settings);
        }
      }
    } catch (error) {
      console.error('Error loading chat settings:', error);
    }
  };

  // Save Chat Settings (nur bei Button-Klick)
  const handleSaveChatSettings = async () => {
    setChatLoading(true);

    try {
      const response = await fetch(createApiUrl('profile/chat-settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ chat_settings: chatSettings })
      });

      const data = await response.json();
      if (data.success) {
        showSuccess(t('profile.chat.updateSuccess'));
      } else {
        showError(t('profile.chat.updateError'));
      }
    } catch (error) {
      showError(t('profile.chat.updateError'));
    } finally {
      setChatLoading(false);
    }
  };

  // Play Sound when selected
  const handleSoundChange = (soundName) => {
    setChatSettings({ ...chatSettings, notificationSound: soundName });
    
    // Spiele Sound einmalig ab
    const audio = new Audio(`${process.env.PUBLIC_URL}/statics/Sounds/${soundName}.mp3`);
    audio.play().catch(err => {
      console.error('Error playing sound:', err);
    });
  };

  // Hilfsfunktion: Vollständigen Namen zusammenstellen
  const getFullName = (user) => {
    if (!user) return 'MTR';
    const parts = [];
    if (user.academic_degree) parts.push(user.academic_degree);
    if (user.first_name) parts.push(user.first_name);
    if (user.last_name) parts.push(user.last_name);
    return parts.length > 0 ? parts.join(' ') : user.username || 'MTR';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" sx={{ mb: 1, color: 'var(--rd-primary)' }}>
        {t('mtr.welcome', 'Willkommen')}, {getFullName(currentUser)}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        {t('mtr.description', 'Verwalten Sie hier Ihre Profil-Einstellungen und Viewer-Präferenzen')}
      </Typography>

        {/* Cards Grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          gap: 3
        }}>
          
          {/* Benutzerinformationen Karte */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'var(--rd-primary)' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t('profile.userInfo', 'Benutzerinformationen')}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('profile.username', 'Benutzername')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {currentUser?.username || 'N/A'}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('profile.fullName', 'Vollständiger Name')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {getFullName(currentUser) !== (currentUser?.username || 'MTR') ? getFullName(currentUser) : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Benutzername ändern Karte */}
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ mr: 1, color: 'var(--rd-primary)' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t('profile.usernameChange.title', 'Benutzername ändern')}
                  </Typography>
                </Box>

                {usernameError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {usernameError}
                  </Alert>
                )}

                {usernameSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {usernameSuccess}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('profile.usernameChange.currentUsername', 'Aktueller Benutzername')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                      {currentUser?.username || 'N/A'}
                    </Typography>
                  </Box>
                  
                  <TextField
                    label={t('profile.usernameChange.newUsername', 'Neuer Benutzername')}
                    value={usernameData.new_username}
                    onChange={(e) => setUsernameData({ new_username: e.target.value })}
                    fullWidth
                    size="small"
                    helperText={t('profile.usernameChange.helperText', 'Mindestens 3 Zeichen')}
                    disabled={currentUser?.username === 'visusadmin'}
                  />
                  
                  {currentUser?.username === 'visusadmin' && (
                    <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                      {t('profile.usernameChange.visusadminProtected', 'Der visusadmin-Benutzername kann nicht geändert werden.')}
                    </Alert>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleUsernameChange}
                  disabled={usernameLoading || !usernameData.new_username || currentUser?.username === 'visusadmin'}
                  startIcon={usernameLoading ? <CircularProgress size={16} /> : null}
                  className="button"
                >
                  {t('profile.usernameChange.changeButton', 'Benutzername ändern')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Passwort ändern Karte */}
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LockIcon sx={{ mr: 1, color: 'var(--rd-primary)' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t('profile.password.title', 'Passwort ändern')}
                  </Typography>
                </Box>

                {passwordError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {passwordError}
                  </Alert>
                )}

                {passwordSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {passwordSuccess}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label={t('profile.password.currentPassword', 'Aktuelles Passwort')}
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    fullWidth
                    size="small"
                  />
                  
                  <TextField
                    label={t('profile.password.newPassword', 'Neues Passwort')}
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    fullWidth
                    size="small"
                  />
                  
                  <TextField
                    label={t('profile.password.confirmPassword', 'Passwort bestätigen')}
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    fullWidth
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handlePasswordChange}
                  disabled={passwordLoading || !passwordData.current_password || !passwordData.new_password}
                  startIcon={passwordLoading ? <CircularProgress size={16} /> : null}
                  className="button"
                >
                  {t('profile.password.changeButton', 'Passwort ändern')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Chat-Benachrichtigungen Karte */}
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ChatIcon sx={{ mr: 1, color: 'var(--rd-primary)' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t('profile.chat.title', 'Chat-Benachrichtigungen')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Mute Toggle Switch */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!chatSettings.muted}
                        onChange={(e) => setChatSettings({ ...chatSettings, muted: !e.target.checked })}
                        disabled={chatLoading}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {chatSettings.muted ? (
                          <VolumeOffIcon sx={{ color: 'text.secondary' }} />
                        ) : (
                          <VolumeUpIcon sx={{ color: 'primary.main' }} />
                        )}
                        <Typography variant="body2">
                          {t('profile.chat.enableNotifications', 'Benachrichtigungstöne')}
                        </Typography>
                      </Box>
                    }
                  />

                  {/* Sound Selector */}
                  <FormControl fullWidth size="small" disabled={chatLoading || chatSettings.muted}>
                    <InputLabel>{t('profile.chat.notificationSound', 'Benachrichtigungston')}</InputLabel>
                    <Select
                      value={chatSettings.notificationSound}
                      label={t('profile.chat.notificationSound', 'Benachrichtigungston')}
                      onChange={(e) => handleSoundChange(e.target.value)}
                    >
                      <MenuItem value="3Dots">3Dots</MenuItem>
                      <MenuItem value="Bright">Bright</MenuItem>
                      <MenuItem value="HappyPop">HappyPop</MenuItem>
                      <MenuItem value="LevelUp">LevelUp</MenuItem>
                      <MenuItem value="Merimba">Merimba</MenuItem>
                      <MenuItem value="Xylo">Xylo</MenuItem>
                    </Select>
                  </FormControl>

                  <Typography variant="caption" color="text.secondary">
                    {t('profile.chat.notificationSoundDescription', 'Wählen Sie den Sound für eingehende Chat-Nachrichten')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveChatSettings}
                  disabled={chatLoading}
                  startIcon={chatLoading ? <CircularProgress size={16} /> : null}
                  className="button"
                >
                  {t('common.save', 'Speichern')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Design/Theme Karte */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {mode === 'dark' ? (
                  <LightbulbIcon sx={{ mr: 1, color: 'var(--rd-primary)' }} />
                ) : (
                  <LightbulbOutlinedIcon sx={{ mr: 1, color: 'var(--rd-primary)' }} />
                )}
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t('theme.title', 'Design')}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('theme.description', 'Wählen Sie zwischen hellem und dunklem Modus')}
              </Typography>

              <Button
                fullWidth
                variant={mode === 'dark' ? 'contained' : 'outlined'}
                onClick={toggleTheme}
                startIcon={mode === 'dark' ? <LightbulbIcon /> : <LightbulbOutlinedIcon />}
                sx={{
                  backgroundColor: mode === 'dark' ? 'var(--rd-primary)' : 'transparent',
                  borderColor: 'var(--rd-primary)',
                  color: mode === 'dark' ? 'var(--rd-primary-contrast)' : 'var(--rd-primary)',
                  '&:hover': {
                    backgroundColor: mode === 'dark' ? 'var(--rd-primary-hover)' : 'color-mix(in srgb, var(--rd-primary) 10%, transparent)',
                  }
                }}
              >
                {mode === 'dark' ? t('theme.lightMode', 'Heller Modus') : t('theme.darkMode', 'Dunkler Modus')}
              </Button>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('mtr.cockpitOrientation', 'Cockpit-Orientierung')}: <strong>{orientation === 'landscape' ? t('mtr.landscape', 'Querformat') : t('mtr.portrait', 'Hochformat')}</strong>
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                onClick={handleOrientationToggle}
                startIcon={<ScreenRotationIcon />}
                sx={{
                  borderColor: 'var(--rd-primary)',
                  color: 'var(--rd-primary)',
                  '&:hover': {
                    backgroundColor: 'color-mix(in srgb, var(--rd-primary) 10%, transparent)',
                    borderColor: 'var(--rd-primary-hover)'
                  }
                }}
              >
                {t('mtr.switchTo', 'Zu {{orientation}} wechseln', { orientation: orientation === 'landscape' ? t('mtr.portrait', 'Hochformat') : t('mtr.landscape', 'Querformat') })}
              </Button>
            </CardContent>
          </Card>

          {/* Viewer-Einstellungen Karte */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SettingsIcon sx={{ mr: 1, color: 'var(--rd-primary)' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t('profile.viewer.title', 'Viewer-Einstellungen')}
                </Typography>
              </Box>

              {viewerError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {viewerError}
                </Alert>
              )}

              {viewerSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {viewerSuccess}
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('profile.viewer.description', 'Wählen Sie Ihren bevorzugten Viewer aus:')}
              </Typography>

              {/* Große Viewer Buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant={viewerPreference === 'web' ? 'contained' : 'outlined'}
                  size="large"
                  onClick={() => handleViewerPreferenceChange('web')}
                  startIcon={<WebIcon />}
                  disabled={viewerLoading}
                  sx={{
                    py: 2,
                    backgroundColor: viewerPreference === 'web' ? 'var(--rd-primary)' : 'transparent',
                    borderColor: 'var(--rd-primary)',
                    color: viewerPreference === 'web' ? 'var(--rd-primary-contrast)' : 'var(--rd-primary)',
                    '&:hover': {
                      backgroundColor: viewerPreference === 'web' ? 'var(--rd-primary-hover)' : 'color-mix(in srgb, var(--rd-primary) 4%, transparent)',
                      borderColor: 'var(--rd-primary-hover)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight="bold">
                      {t('profile.viewer.webViewer', 'Web Viewer')}
                    </Typography>
                    <Typography variant="caption">
                      {t('profile.viewer.browserBased', 'Browser-basiert')}
                    </Typography>
                  </Box>
                </Button>

                <Button
                  variant={viewerPreference === 'client' ? 'contained' : 'outlined'}
                  size="large"
                  onClick={() => handleViewerPreferenceChange('client')}
                  startIcon={<DesktopIcon />}
                  disabled={viewerLoading}
                  sx={{
                    py: 2,
                    backgroundColor: viewerPreference === 'client' ? 'var(--rd-primary)' : 'transparent',
                    borderColor: 'var(--rd-primary)',
                    color: viewerPreference === 'client' ? 'var(--rd-primary-contrast)' : 'var(--rd-primary)',
                    '&:hover': {
                      backgroundColor: viewerPreference === 'client' ? 'var(--rd-primary-hover)' : 'color-mix(in srgb, var(--rd-primary) 4%, transparent)',
                      borderColor: 'var(--rd-primary-hover)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight="bold">
                      {t('profile.viewer.clientViewer', 'Client Viewer')}
                    </Typography>
                    <Typography variant="caption">
                      {t('profile.viewer.desktopApplication', 'Desktop-Anwendung')}
                    </Typography>
                  </Box>
                </Button>

                {viewerLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Arbeitsplatz-Auswahl Karte */}
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ComputerIcon sx={{ mr: 1, color: 'var(--rd-primary)' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {t('mtr.workstation.title', 'Arbeitsplatz')}
                  </Typography>
                </Box>

                {/* Current Workstation Display */}
                {currentWorkstation ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('mtr.workstation.currentWorkstation', 'Aktueller Arbeitsplatz')}
                    </Typography>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: 'color-mix(in srgb, var(--rd-primary) 10%, transparent)', 
                      borderRadius: 1,
                      border: '1px solid var(--rd-primary)',
                      mt: 1
                    }}>
                      <Typography variant="body1" fontWeight="bold" color="var(--rd-primary)">
                        {currentWorkstation.short_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {currentWorkstation.name}
                      </Typography>
                      {currentWorkstation.modality && (
                        <Typography variant="caption" color="text.secondary">
                          {currentWorkstation.modality}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('mtr.workstation.description', 'Wählen Sie Ihren Arbeitsplatz aus:')}
                    </Typography>
                    
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('mtr.workstation.selectWorkstation', 'Arbeitsplatz wählen')}</InputLabel>
                      <Select
                        value={selectedWorkstation}
                        label={t('mtr.workstation.selectWorkstation', 'Arbeitsplatz wählen')}
                        onChange={(e) => setSelectedWorkstation(e.target.value)}
                        disabled={workstationLoading}
                      >
                        <MenuItem value="">
                          <em>{t('common.none', 'Keine')}</em>
                        </MenuItem>
                        {workstations.filter(ws => ws.is_active).map((ws) => (
                          <MenuItem key={ws.id} value={ws.id}>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {ws.short_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {ws.name}
                                {ws.modality && ` (${ws.modality})`}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                {currentWorkstation ? (
                  <Button
                    variant="outlined"
                    onClick={handleWorkstationLogout}
                    disabled={workstationLoading}
                    startIcon={workstationLoading ? <CircularProgress size={16} /> : <LogoutIcon />}
                    className="button-cancel"
                  >
                    {t('mtr.workstation.logout', 'Ausloggen')}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleWorkstationLogin}
                    disabled={workstationLoading || !selectedWorkstation}
                    startIcon={workstationLoading ? <CircularProgress size={16} /> : <LoginIcon />}
                    className="button"
                  >
                    {t('mtr.workstation.login', 'Einloggen')}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
      </Box>
    </Container>
  );
};

export default MTRDashboard;
